import { useState } from 'react';

import { useApiKeys, useCreateApiKey, useRevokeApiKey, type ApiKeyRow } from '../../api/queries.js';
import { useAuthStore } from '../../auth/auth-store.js';
import {
  Button,
  type Column,
  ConfirmModal,
  DataTable,
  DrawerPanel,
  Field,
  IconButton,
  Input,
  PageHeader,
  Select,
  formatDate,
  timeAgo,
} from '../../components/ui/index.js';

export function SettingsApiKeys(): JSX.Element {
  const user = useAuthStore((s) => s.user);
  const slug = user?.workspaceSlug;
  const keysQ = useApiKeys(slug);
  const createKey = useCreateApiKey(slug);
  const revokeKey = useRevokeApiKey(slug);

  const [showCreate, setShowCreate] = useState(false);
  const [keyName, setKeyName] = useState('');
  const [scope, setScope] = useState('read');
  const [expiresAt, setExpiresAt] = useState('');
  const [rawKey, setRawKey] = useState<string | null>(null);
  const [revokeId, setRevokeId] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  async function handleCreate(): Promise<void> {
    if (!keyName.trim()) return;
    const result = await createKey.mutateAsync({
      name: keyName.trim(),
      scope,
      expires_at: expiresAt || undefined,
    });
    setRawKey(result.raw_key);
    setShowCreate(false);
    setKeyName('');
    setScope('read');
    setExpiresAt('');
  }

  function copyKey(): void {
    if (!rawKey) return;
    void navigator.clipboard.writeText(rawKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  const columns: Column<ApiKeyRow>[] = [
    {
      key: 'name',
      label: 'Название',
      render: (r) => <span style={{ fontWeight: 500 }}>{r.name}</span>,
    },
    {
      key: 'prefix',
      label: 'Префикс',
      width: 130,
      render: (r) => <span style={{ fontFamily: 'var(--inbx-font-mono)' }}>{r.prefix}…</span>,
    },
    {
      key: 'scope',
      label: 'Скоуп',
      width: 130,
      render: (r) => <span className="inbx-pill-badge inbx-pill-badge--info">{r.scope}</span>,
    },
    { key: 'created', label: 'Создан', width: 130, render: (r) => formatDate(r.createdAt) },
    {
      key: 'expires',
      label: 'Истекает',
      width: 130,
      render: (r) => (r.expiresAt ? formatDate(r.expiresAt) : '∞'),
    },
    { key: 'used', label: 'Последний раз', width: 140, render: (r) => timeAgo(r.lastUsedAt) },
    {
      key: 'actions',
      label: '',
      width: 80,
      render: (r) => (
        <span style={{ display: 'flex', gap: 2 }}>
          <IconButton icon="ti-trash" tooltip="Отозвать" onClick={() => setRevokeId(r.id)} />
        </span>
      ),
    },
  ];

  return (
    <div className="inbx-page">
      <PageHeader
        title="API-ключи"
        actions={
          <Button variant="primary" icon="ti-plus" onClick={() => setShowCreate(true)}>
            Создать ключ
          </Button>
        }
      />
      <div className="inbx-page__body" style={{ padding: 0 }}>
        <DataTable rows={keysQ.data ?? []} columns={columns} loading={keysQ.isLoading} />
      </div>

      <DrawerPanel
        open={showCreate}
        title="Новый API-ключ"
        onClose={() => setShowCreate(false)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowCreate(false)}>
              Отмена
            </Button>
            <Button
              variant="primary"
              onClick={handleCreate}
              disabled={createKey.isPending || !keyName.trim()}
            >
              {createKey.isPending ? 'Создание…' : 'Создать'}
            </Button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Field label="Название" required>
            <Input
              value={keyName}
              onChange={(e) => setKeyName(e.target.value)}
              placeholder="Мой интеграционный ключ"
              autoFocus
            />
          </Field>
          <Field label="Права доступа">
            <Select value={scope} onChange={(e) => setScope(e.target.value)}>
              <option value="read">read — только чтение</option>
              <option value="write">write — чтение и запись</option>
              <option value="admin">admin — полный доступ</option>
            </Select>
          </Field>
          <Field label="Срок действия" hint="оставьте пустым для бессрочного">
            <Input type="date" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} />
          </Field>
        </div>
      </DrawerPanel>

      {rawKey && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
        >
          <div
            className="inbx-card"
            style={{ width: 480, padding: 24, display: 'flex', flexDirection: 'column', gap: 16 }}
          >
            <h3 style={{ margin: 0 }}>API-ключ создан</h3>
            <div style={{ color: 'var(--inbx-warning)', fontSize: 'var(--inbx-fz-sm)' }}>
              Сохраните ключ — он показывается только один раз
            </div>
            <div
              style={{
                fontFamily: 'var(--inbx-font-mono)',
                background: 'var(--inbx-bg-secondary)',
                padding: '10px 14px',
                borderRadius: 'var(--inbx-radius)',
                wordBreak: 'break-all',
                fontSize: 'var(--inbx-fz-sm)',
              }}
            >
              {rawKey}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button variant="secondary" icon="ti-copy" onClick={copyKey}>
                {copied ? 'Скопировано!' : 'Копировать'}
              </Button>
              <Button variant="primary" onClick={() => setRawKey(null)}>
                Готово
              </Button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        open={!!revokeId}
        title="Отозвать API-ключ?"
        description="Ключ будет немедленно аннулирован. Все интеграции с этим ключом перестанут работать."
        confirmLabel="Отозвать"
        variant="danger"
        onConfirm={() => {
          if (revokeId) revokeKey.mutate(revokeId);
          setRevokeId(null);
        }}
        onCancel={() => setRevokeId(null)}
      />
    </div>
  );
}
